import { useCallback, useEffect, useState } from "react";
import { Check, Eye, FileText, Inbox, X } from "lucide-react";
import { Empty, Modal, StatusPill, useBlobUrl } from "../../components/common";
import { api, dateTime, errMsg, idr } from "../../lib/api";

const TradingAccount = ({ payment }) => {
  const [pw, setPw] = useState(null);
  const [err, setErr] = useState("");
  const a = payment.trading_account;
  const reveal = () => api.get(`/admin/payments/${payment.id}/trading-credentials`).then((r) => setPw(r.data.password)).catch((e) => setErr(errMsg(e, "Password tidak dapat ditampilkan")));
  return (
    <div className="trading-box" data-testid="proof-trading-account">
      <p className="eyebrow">AKUN TRADING UNTUK PEMASANGAN EA</p>
      <div><span>Platform</span><b>{a.platform}</b></div>
      <div><span>Nomor akun</span><b>{a.login}</b></div>
      <div><span>Server</span><b>{a.server}</b></div>
      <div><span>Password</span>{pw ? <b data-testid="proof-trading-password">{pw}</b> : <button type="button" className="outline sm" onClick={reveal} data-testid="reveal-trading-password">Tampilkan password</button>}</div>
      {err && <small className="down" data-testid="reveal-trading-error">{err}</small>}
    </div>
  );
};

const ProofViewer = ({ payment, onClose, onReviewed }) => {
  const { url, failed } = useBlobUrl(`/admin/payments/${payment.id}/proof`);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const review = async (action) => {
    setBusy(true);
    try {
      await api.post(`/admin/payments/${payment.id}/review`, { action, note });
      onReviewed();
    } catch (err) {
      setError(errMsg(err, "Gagal memproses pembayaran"));
    } finally {
      setBusy(false);
    }
  };
  const isPdf = payment.proof_type === "application/pdf" || payment.filename?.toLowerCase().endsWith(".pdf");
  return (
    <Modal wide onClose={onClose} testId="proof-modal">
      <p className="eyebrow">{payment.kind === "ea_rental" ? "SEWA EA" : "PROFIT SHARING"} · {dateTime(payment.created_at)}</p>
      <h3>{payment.title}</h3>
      <p className="muted">{payment.member?.name} · {payment.member?.email} · {payment.member?.phone}</p>
      <div className="proof-frame" data-testid="proof-preview">
        {url ? (isPdf ? <iframe title="bukti" src={url} /> : <img src={url} alt="Bukti pembayaran" />) : <span className="muted">{failed ? "Bukti tidak dapat dimuat" : "Memuat bukti…"}</span>}
      </div>
      {url && <a className="text-button" href={url} target="_blank" rel="noreferrer" data-testid="proof-open-link"><FileText size={13} /> Buka di tab baru</a>}
      {payment.trading_account && <TradingAccount payment={payment} />}
      <div className="price-line"><span>Nominal yang harus masuk</span><b data-testid="proof-amount">{idr(payment.amount)}</b></div>
      {payment.unique_code && <p className="muted" data-testid="proof-unique-code">Harga {idr(payment.base_amount)} + kode unik <b className="code-hl">{payment.unique_code}</b> — cocokkan dengan mutasi QRIS.</p>}
      {payment.status === "pending" ? (
        <>
          <label className="field">Catatan (opsional)<input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Contoh: nominal tidak sesuai" data-testid="review-note-input" /></label>
          {error && <div className="error" data-testid="review-error">{error}</div>}
          <div className="row-actions">
            <button className="danger" disabled={busy} onClick={() => review("reject")} data-testid="reject-payment-button"><X size={15} /> Tolak</button>
            <button className="primary" disabled={busy} onClick={() => review("approve")} data-testid="approve-payment-button"><Check size={15} /> Setujui</button>
          </div>
        </>
      ) : <p className="muted">Status: <StatusPill status={payment.status} /> oleh {payment.reviewed_by} {payment.review_note && `· ${payment.review_note}`}</p>}
    </Modal>
  );
};

export default function AdminPayments({ onChange }) {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tick, setTick] = useState(0);
  const load = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    let alive = true;
    api.get("/admin/payments", { params: status ? { status } : {} }).then((r) => alive && setRows(r.data));
    return () => { alive = false; };
  }, [status, tick]);

  return (
    <>
      <div className="page-intro">
        <div><p className="eyebrow">ADMIN · VERIFIKASI QRIS</p><h2>Verifikasi pembayaran</h2><p className="muted">Periksa bukti transfer. Persetujuan sewa EA mengaktifkan EA 30 hari dan mencatat komisi affiliate 5%.</p></div>
      </div>
      <div className="seg tabs">
        {[["pending", "Menunggu"], ["approved", "Disetujui"], ["rejected", "Ditolak"], ["", "Semua"]].map(([v, l]) => (
          <button key={v || "all"} className={status === v ? "active" : ""} onClick={() => setStatus(v)} data-testid={`admin-filter-${v || "all"}`}>{l}</button>
        ))}
      </div>
      <section className="panel table-panel">
        {rows.length === 0 ? <Empty testId="admin-payments-empty" icon={Inbox} title="Tidak ada pembayaran" text="Belum ada bukti pembayaran pada filter ini." /> : (
          <>
            <div className="table-row cols-5 table-head"><span>TANGGAL</span><span>MEMBER</span><span>PEMBAYARAN</span><span>NOMINAL</span><span>STATUS</span></div>
            {rows.map((p) => (
              <div className="table-row cols-5" key={p.id} data-testid={`admin-payment-row-${p.id}`}>
                <span>{dateTime(p.created_at)}</span>
                <span>{p.member?.name}<small className="muted block">{p.member?.email}</small></span>
                <span>{p.title}<small className="muted block">{p.kind === "ea_rental" ? "Sewa EA" : "Profit sharing"}</small></span>
                <span>{idr(p.amount)}{p.unique_code && <small className="muted block">kode <b className="code-hl">{p.unique_code}</b></small>}</span>
                <span className="row-end"><StatusPill status={p.status} /><button className="outline sm" onClick={() => setSelected(p)} data-testid={`admin-view-proof-${p.id}`}><Eye size={13} /> Periksa</button></span>
              </div>
            ))}
          </>
        )}
      </section>
      {selected && <ProofViewer payment={selected} onClose={() => setSelected(null)} onReviewed={() => { setSelected(null); load(); onChange?.(); }} />}
    </>
  );
}
